import {InputText} from '@/components/commons/input-text/InputText'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import i18n, {useTranslation} from '@/translations/i18n'
import {zodResolver} from '@hookform/resolvers/zod'
import {useForm} from 'react-hook-form'
import {z} from 'zod'
import {AuthUser} from '@/features/auth/types'
import {useAccountStore} from '@/features/account/store/useAccountStore'
import {useChangeAccountEmail} from '@/features/account/services/useChangeAccountEmail'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useAuthStore} from '@/features/auth/store/store'
import {toast} from '@/utilities/toast/toast'

const ChangeEmailValidationSchema = z.object({
    email: z.string().email({
        message: i18n.t('errors:email_error')
    }),
    password: z.string().min(1, {
        message: i18n.t('errors:required')
    })
})
type ChangeEmailValidationSchema = z.infer<typeof ChangeEmailValidationSchema>

export const ChangeEmailForm = ({userId}: {userId: AuthUser['id']}) => {
    const {t} = useTranslation()
    const setAuthUser = useAuthStore(state => state.setAuthUser)
    const setEditingSection = useAccountStore(state => state.setEditingSection)

    const form = useForm<ChangeEmailValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(ChangeEmailValidationSchema)
    })

    const changeAccountEmailMutation = useChangeAccountEmail({
        urlParams: {userId},
        options: {
            onError: error => {
                console.error(error)
                if (error.response?.data.code == 'password-mismatch') {
                    toast.error(t('account:password_not_valid'))
                    form.setError('password', {message: t('account:password_not_valid')})
                }
            },
            onSuccess: data => {
                setAuthUser(data)
                setEditingSection(null)
            }
        }
    })

    const handleSubmit = form.handleSubmit(
        formValues => changeAccountEmailMutation.mutate(formValues),
        errors => console.error(errors)
    )

    return (
        <Flexbox render="form" direction="column" gap={7} align="flex-end" fullWidth onSubmit={handleSubmit}>
            <Flexbox direction="column" gap={3} fullWidth>
                <InputText
                    type="email"
                    label={t('account:new_email')}
                    placeholder={t('commons:email_placeholder')}
                    errorMessage={form.formState.errors.email?.message}
                    {...form.register('email')}
                />
                <InputText
                    type="password"
                    label={t('commons:password')}
                    placeholder={'••••••••••'}
                    helpText={t('account:enter_current_password')}
                    errorMessage={form.formState.errors.password?.message}
                    visibilityToggle
                    {...form.register('password')}
                />
            </Flexbox>
            <Button disabled={changeAccountEmailMutation.isPending} type="submit" variant="primaryNeutral">
                {t('account:change_email')}
                {changeAccountEmailMutation.isPending && <Spinner />}
            </Button>
        </Flexbox>
    )
}
